import USStatesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/USStatesWithISOCodes';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { ReactNode, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu } from '@/pages/Search/SearchPage/types';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import { FilterAction } from '@/types/SearchFacet';
import { CanadianProvincesWithISOCodes } from '@/utils/address';
import {
    modifyParamsRemovePage,
    modifyParamsRemoveState,
    modifyParamsReplaceState,
    parseQueryParams,
} from '@/utils/queryParams';

type StateOptionType = OptionType & { count?: number };

type Props = {
    countryCode: string;
    isAllFilters?: boolean;
};

export const StateFilter = ({ countryCode, isAllFilters }: Props) => {
    const { formatMessage } = useIntl();
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const currentStateParam = queryParams[paramTypes.STATE_ID] ?? '';

    const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Location;

    const isUS = countryCode === 'US';
    const labelText = formatMessage({ id: isUS ? 'searchPage.facets.state' : 'searchPage.facets.province' });

    const regionFacet = useAppSelector((state) => getMergedOptionFacet(state, FacetTypes.REGION_ID));

    const hardcodedRegions = isUS ? USStatesWithISOCodes : CanadianProvincesWithISOCodes;

    const stateOptions: StateOptionType[] = useMemo(() => {
        const allLabel = formatMessage({ id: isUS ? 'searchPage.facets.allStates' : 'searchPage.facets.allProvinces' });
        const facetOptions = regionFacet?.options ?? [];

        if (facetOptions.length === 0) {
            const options = hardcodedRegions.map(({ code, name }) => ({ label: name, value: code }));
            return [{ label: allLabel, value: '' }, ...options];
        }

        const regionCodeToName = new Map(hardcodedRegions.map(({ code, name }) => [code, name]));

        const options: StateOptionType[] = facetOptions
            .filter((option) => {
                if (!regionCodeToName.has(option.id)) {
                    return false;
                }
                if (option.id === currentStateParam) {
                    return true;
                }
                return (option.count ?? 0) > 0;
            })
            .sort((firstOption, secondOption) => (secondOption.count ?? 0) - (firstOption.count ?? 0))
            .map((option) => ({
                count: option.count ?? 0,
                label: regionCodeToName.get(option.id) ?? option.name,
                value: option.id,
            }));

        return [{ label: allLabel, value: '' }, ...options];
    }, [currentStateParam, formatMessage, hardcodedRegions, isUS, regionFacet]);

    const selectedOption = stateOptions.find((opt) => opt.value === currentStateParam) ?? stateOptions[0];

    const formatStateOptionLabel = useCallback((option: OptionType): ReactNode => {
        const stateOption = option as StateOptionType;
        if (stateOption.value === '' || stateOption.count === undefined) {
            return <span>{stateOption.label}</span>;
        }
        return (
            <span className="flex w-full items-center justify-between">
                <span>{stateOption.label}</span>
                <span className="text-text-secondary">{getRoundedNumberText({ totalFound: stateOption.count })}</span>
            </span>
        );
    }, []);

    const onStateChange = useCallback(
        (option: OptionType | null) => {
            if (!option || option.value === '') {
                let newParams = modifyParamsRemoveState(queryParams);
                newParams = modifyParamsRemovePage(newParams);

                navigateWithParams({
                    checkForPageRemoval: true,
                    currentOpenMenu: keepMenuOpenValue,
                    filterAction: FilterAction.LocationRemoved,
                    params: newParams,
                    preventScrollReset: true,
                });
            } else {
                let newParams = modifyParamsReplaceState(queryParams, String(option.value));
                newParams = modifyParamsRemovePage(newParams);

                navigateWithParams({
                    checkForPageRemoval: true,
                    currentOpenMenu: keepMenuOpenValue,
                    filterAction: FilterAction.LocationChanged,
                    params: newParams,
                    preventScrollReset: true,
                });
            }
        },
        [keepMenuOpenValue, navigateWithParams, queryParams]
    );

    return (
        <div className="mt-xs">
            <Select
                aria-label={formatMessage({
                    id: isUS ? 'searchPage.facets.selectState' : 'searchPage.facets.selectProvince',
                })}
                data-testid="location-state-select"
                formatOptionLabel={formatStateOptionLabel}
                instanceId="state-filter"
                labelText={labelText}
                name="stateFilter"
                onChange={onStateChange}
                options={stateOptions}
                small
                usePortalMenu
                value={selectedOption}
            />
        </div>
    );
};
